import { useQueryClient } from '@tanstack/react-query';
import { CrewMerchantApi } from 'corso-types';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';
import api from '~/api';
import { useStoreId } from '~/providers/StoreUserProvider';
import {
  convertStateToSearch,
  useSearchState,
} from './searchControlBar/SearchContext';
import useSearchData from './useSearchData';

type Search = Omit<
  CrewMerchantApi.RequestBody<'/:storeId/search', 'post'>,
  'take' | 'cursor'
>;

type Kind = Exclude<Search['kind'], 'Order'>;

type Cursor = { id: number; direction: 'prev' | 'next' };

const entityPath = {
  Return: 'claims/return',
  Warranty: 'claims/warranty',
  Registration: 'registrations',
  Shipping: 'claims/shipping',
  Product: 'products',
} satisfies Record<Kind, string>;

export function useNavigateSearchData({
  currentId,
  kind,
}: {
  currentId: number;
  kind: Kind;
}) {
  const [searchState, dispatch] = useSearchState();
  const storeId = useStoreId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const search = useMemo(
    () => convertStateToSearch(searchState),
    [searchState],
  );
  const { data, pageInfo, isFetching } = useSearchData(search);
  const currentIndex = data.findIndex((row) => row.id === currentId);

  const goTo = (id: number) => {
    navigate(`/${storeId}/${entityPath[kind]}/${id}`);
  };

  const pageQueryKey = (cursor: Cursor) =>
    ['search', { ...search, cursor }, storeId] as const;

  const pageQueryFn = (cursor: Cursor) =>
    api.store(storeId).search({
      take: 50,
      ...search,
      cursor,
      shouldUseOpenSearch: false,
    });

  useEffect(() => {
    if (currentIndex < 0) return;

    const prefetchEntity = (id: number) =>
      match(kind)
        .with('Return', 'Warranty', () =>
          queryClient.prefetchQuery({
            queryKey: ['claim', id, storeId],
            queryFn: () => api.store(storeId).claim(`${id}`).get(),
          }),
        )
        .with('Registration', () =>
          queryClient.prefetchQuery({
            queryKey: ['registration', id, storeId],
            queryFn: () =>
              api.store(storeId).registrations.get({ registrationId: `${id}` }),
          }),
        )
        .with('Shipping', () =>
          queryClient.prefetchQuery({
            queryKey: ['shippingClaim', id, storeId],
            queryFn: () =>
              api.store(storeId).shippingProtection.claim(`${id}`).get(),
          }),
        )
        .with('Product', () =>
          queryClient.prefetchQuery({
            queryKey: ['product', id, storeId],
            queryFn: () =>
              api.store(storeId).product.get({ storeProductId: `${id}` }),
          }),
        )
        .exhaustive();

    const prefetchPage = (cursor: Cursor) =>
      queryClient
        .prefetchQuery({
          queryKey: ['search', { ...search, cursor }, storeId],
          queryFn: () =>
            api.store(storeId).search({
              take: 50,
              ...search,
              cursor,
              shouldUseOpenSearch: false,
            }),
        })
        .catch(console.error);

    const prev = data[currentIndex - 1];
    const next = data[currentIndex + 1];
    if (prev) prefetchEntity(prev.id).catch(console.error);
    if (next) prefetchEntity(next.id).catch(console.error);

    if (currentIndex === data.length - 1 && pageInfo.nextCursor !== null) {
      prefetchPage({ id: pageInfo.nextCursor, direction: 'next' }).catch(
        console.error,
      );
    }
    if (currentIndex === 0 && pageInfo.prevCursor !== null) {
      prefetchPage({ id: pageInfo.prevCursor, direction: 'prev' }).catch(
        console.error,
      );
    }
  }, [
    currentIndex,
    data,
    pageInfo.nextCursor,
    pageInfo.prevCursor,
    kind,
    storeId,
    queryClient,
    search,
  ]);

  const resolve = (direction: 'prev' | 'next') => {
    const offset = direction === 'prev' ? -1 : 1;

    const neighbor =
      currentIndex >= 0 ? data[currentIndex + offset] : undefined;

    if (neighbor) return { onAction: () => goTo(neighbor.id), disabled: false };

    const atEdge =
      direction === 'prev'
        ? currentIndex === 0
        : currentIndex === data.length - 1;

    const cursorId =
      direction === 'prev' ? pageInfo.prevCursor : pageInfo.nextCursor;

    if (!atEdge || cursorId === null)
      return { onAction: () => {}, disabled: true };

    const cursor = { id: cursorId, direction } as const;

    return {
      onAction: () => {
        queryClient
          .ensureQueryData({
            queryKey: pageQueryKey(cursor),
            queryFn: () => pageQueryFn(cursor),
          })
          .then((page) => {
            const target =
              direction === 'prev' ? page.data.at(-1) : page.data[0];

            if (!target) return;

            dispatch({ type: 'setCursor', cursor });
            goTo(target.id);
          })
          .catch(console.error);
      },
      disabled: isFetching,
    };
  };

  return { previous: resolve('prev'), next: resolve('next') };
}
